import React from 'react';
import type { ColumnDefinition } from '../../../../components/Table/columns/shared/column-types';
import type { CustomColumnDef } from '../../../../components/Table/types';

function extractHeaderText(node: unknown): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node).trim();
  if (Array.isArray(node)) return node.map(extractHeaderText).filter(Boolean).join(' ').trim();
  if (React.isValidElement(node)) {
    return extractHeaderText((node.props as { children?: unknown })?.children);
  }
  return '';
}

const NUMERIC_VALUE_PATTERN = /^-?\d+(\.\d+)?([eE][-+]?\d+)?$/;

function formatSummaryMatchNumericValue(value: number): string {
  const rounded = Math.round((value + Number.EPSILON) * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(2);
}

export function formatSummaryMatchCellValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ') || '-';

  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatSummaryMatchNumericValue(value);
  }

  const text = String(value).trim();
  if (!text) return '-';
  if (NUMERIC_VALUE_PATTERN.test(text)) {
    return formatSummaryMatchNumericValue(Number(text));
  }

  return text;
}

const cellRendererCache = new Map<string, CustomColumnDef['render']>();

function getSummaryMatchCellRenderer(columnName: string): CustomColumnDef['render'] {
  const cached = cellRendererCache.get(columnName);
  if (cached) return cached;

  const renderer: CustomColumnDef['render'] = ({ data, value }) =>
    formatSummaryMatchCellValue(data?.[columnName] ?? value);
  cellRendererCache.set(columnName, renderer);
  return renderer;
}

type BuildSummaryMatchGridColumnsParams = {
  allColumns: ColumnDefinition[];
  summaryColumnVisibilityModel: Record<string, boolean>;
  summaryColumnOrder?: string[];
  sourceWeights?: Array<{ name: string; score: number }>;
  tab: string;
  view: string;
  source: string;
  includeHidden?: boolean;
};

export function buildSummaryMatchGridColumns({
  allColumns,
  summaryColumnVisibilityModel,
  summaryColumnOrder,
  sourceWeights = [],
  tab,
  view,
  source,
  includeHidden = false,
}: BuildSummaryMatchGridColumnsParams): CustomColumnDef[] {
  const configuredNames = new Set(
    Object.keys(summaryColumnVisibilityModel).filter(name => name !== 'evidenceTimeline'),
  );
  const visibleNames = new Set(
    Object.entries(summaryColumnVisibilityModel)
      .filter(([name, visible]) => visible && name !== 'evidenceTimeline')
      .map(([name]) => name),
  );
  const targetNames = includeHidden ? configuredNames : visibleNames;

  const orderedKeys = summaryColumnOrder?.length
    ? [
        ...summaryColumnOrder.filter(name => targetNames.has(name)),
        ...[...targetNames].filter(name => !summaryColumnOrder.includes(name)),
      ]
    : [...targetNames];

  return orderedKeys
    .map(name => allColumns.find(col => col.name === name))
    .filter((col): col is ColumnDefinition => {
      if (!col?.name) return false;
      if (col.name === 'sourceScore' && sourceWeights.length === 0) return false;
      if (typeof col.show === 'function') {
        return col.show({ tab, view, isEvidence: false, source });
      }
      if (col.show !== undefined) return col.show;
      return true;
    })
    .map(col => ({
      name: col.name!,
      header: extractHeaderText(col.header) || col.name!,
      sortable: col.enableSorting !== false,
      enableColumnFilter: false,
      minWidth: col.minWidth ?? 150,
      category: col.category,
      render: getSummaryMatchCellRenderer(col.name!),
    }));
}
